/**
 * Pure conversions from `GET /offers/:offerId/availability`'s unit+range
 * scoped response into the shared shapes the merged table's availability
 * columns and INSPECT popover consume (offer-unified-table, CU-86cbc3r8b).
 *
 * Every lookup here matches by DATE-RANGE OVERLAP against the server's own
 * period boundaries, never by an exact period-id string — the merged table
 * renders periods at a caller-chosen granularity (day/week/half-month/month)
 * independent of whatever granularity the server response's own `periods`
 * happen to use, so an exact-id match would silently miss data whenever the
 * two disagree (the same limitation the catalog's own
 * `mapAvailabilityResponseToEntriesGrid` has, deriving period ids from a
 * YYYY-MM prefix that only works at month granularity — not repeated here).
 */
import type {
  AvailabilityCell,
  AvailabilityPeriodCell,
  CellEntry,
  UnitAvailabilityRow,
} from '@ooh/contracts';
import {
  cellEntryToCellState,
  INVENTORY_OCCUPANCY_STATE_VALUES,
  isHeldEntryStatus,
  rangesOverlap,
  type InventoryOccupancyByRowId,
  type InventoryOccupancyState,
  buildEntryOwnerHint,
  type CellState,
  type MultiSelectCellRef,
} from '@ooh/screens';
import type { UnifiedSelectCellResolution } from '@ooh/screens';

const INVENTORY_OCCUPANCY_STATES: ReadonlySet<CellState> = new Set<InventoryOccupancyState>(
  INVENTORY_OCCUPANCY_STATE_VALUES,
);

/** Narrows a CellEntry status to a reservation status, or null for non-reservation states. */
function toReservationStatus(
  status: CellEntry['status'],
): 'SOFT_RESERVATION' | 'HARD_RESERVATION' | null {
  return status === 'SOFT_RESERVATION' || status === 'HARD_RESERVATION' ? status : null;
}

function canonicalEntryToOccupancyState(entry: CellEntry): InventoryOccupancyState | null {
  const state = cellEntryToCellState(entry);
  return INVENTORY_OCCUPANCY_STATES.has(state) ? (state as InventoryOccupancyState) : null;
}

/**
 * Groups one server period's `entries[]` by their own `dateFrom`/`dateTo` sub-range
 * (CU-86cbh03pj) — entries sharing the SAME sub-range (or both lacking one, i.e. a
 * base-state/availability-window entry with no reservation row) are CO-EXISTING states
 * at that one window and stay together (precedence still picks one dominant entry per
 * group); entries with DIFFERENT sub-ranges are genuinely different windows within the
 * same period and must not be collapsed into one. Before partial release existed, a
 * single hold could never leave a gap inside one server period, so every entry in a
 * period always shared the period's own boundaries and this grouping was a no-op;
 * a shortened/split hold breaks that assumption. Preserves each group's relative
 * ARRAY order (the server already sorts `entries[]` by precedence desc), so `group[0]`
 * remains the correct dominant pick within its own sub-range.
 */
function groupEntriesByOwnRange(entries: CellEntry[]): CellEntry[][] {
  const groups = new Map<string, CellEntry[]>();
  for (const entry of entries) {
    const key =
      entry.dateFrom !== undefined && entry.dateTo !== undefined
        ? `${entry.dateFrom}:${entry.dateTo}`
        : 'base';
    const group = groups.get(key);
    if (group) {
      group.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  }
  return [...groups.values()];
}

/**
 * Resolves one entry's own occupancy range, clamped to the enclosing server period's
 * boundaries — an entry backed by a reservation row (`dateFrom`/`dateTo` present) can
 * span more calendar time than the ONE period it's being read from (e.g. a hold running
 * across two monthly periods carries its FULL range on the entry in both), so each
 * period's own contribution must intersect the entry's range with `[period.start,
 * period.end]`, not use either bound unclamped. A base-state/availability-window entry
 * (no `dateFrom`/`dateTo`) genuinely spans the whole period — falls back to the
 * period's own boundaries, matching the pre-CU-86cbh03pj behavior exactly.
 */
function clampEntryRangeToPeriod(
  entry: CellEntry,
  period: { start: string; end: string },
): { start: string; end: string } {
  return {
    start:
      entry.dateFrom !== undefined && entry.dateFrom > period.start ? entry.dateFrom : period.start,
    end: entry.dateTo !== undefined && entry.dateTo < period.end ? entry.dateTo : period.end,
  };
}

/**
 * Pushes one server period's occupancy range(s) — one per `groupEntriesByOwnRange`
 * group — onto `ranges`. Extracted out of `mapOfferUnitsToOccupancy`'s loop body so
 * that function's own nesting depth stays under the lint budget (`for unit { for
 * period { ... } }` instead of a third/fourth nested `for`/`if` level here).
 */
function pushPeriodOccupancyRanges(
  period: AvailabilityPeriodCell,
  ranges: InventoryOccupancyByRowId[string],
): void {
  for (const group of groupEntriesByOwnRange(period.entries)) {
    // Dominant entry within its own sub-range group: first in the group (API sorts
    // entries[] by precedence desc — see groupEntriesByOwnRange's doc comment for
    // why grouping preserves that order).
    const dominant = group[0];
    if (!dominant) continue;
    const state = canonicalEntryToOccupancyState(dominant);
    if (state === null) continue;
    const { start, end } = clampEntryRangeToPeriod(dominant, period);
    ranges.push({
      start,
      end,
      state,
      ownerHint: buildEntryOwnerHint(dominant),
      conditional: dominant.conditional === true,
      // The reservation's OWN status / deadline — distinct from start/end above
      // (this range's own, possibly-shrunk-from-the-period boundaries). Undefined/
      // null for a base-state or availability-window dominant entry, which has no
      // reservation. Mirrors `mapAvailabilityResponseToOccupancy`
      // (media-units/api/queries.ts), the catalog's own analog of this function.
      reservationStatus: dominant.status,
      ttlExpiresAt: dominant.ttlExpiresAt ?? null,
    });
  }
}

/**
 * Converts the offer availability response into `InventoryOccupancyByRowId`,
 * keyed by `String(unitId)` (the offer-unit id — the merged table's own row
 * id), for `buildRichAvailabilityColumns` to overlap against any rendered
 * period array.
 */
export function mapOfferUnitsToOccupancy(units: UnitAvailabilityRow[]): InventoryOccupancyByRowId {
  const result: InventoryOccupancyByRowId = {};
  for (const unit of units) {
    const rowId = String(unit.unitId);
    const ranges: InventoryOccupancyByRowId[string] = [];
    for (const period of unit.periods) {
      pushPeriodOccupancyRanges(period, ranges);
    }
    result[rowId] = ranges;
  }
  return result;
}

/** Finds the offer unit's own server period overlapping `[start, end]` and returns its entries. */
export function findOfferUnitEntries(
  units: UnitAvailabilityRow[],
  unitId: string,
  start: string,
  end: string,
): CellEntry[] {
  const unit = units.find((u) => String(u.unitId) === unitId);
  if (!unit) return [];
  const period = unit.periods.find((p) => rangesOverlap(p.start, p.end, start, end));
  return period?.entries ?? [];
}

/**
 * Resolves an `AvailabilityCell` for the INSPECT popover's quick actions
 * (upgrade/close-sale/cancel/force-release) — this is NOT restricted to the
 * caller's own holds: `force-release` exists
 * specifically to act on ANOTHER user's hold, so filtering by
 * `ownership === 'mine'` would make every quick action a no-op against any
 * hold the caller doesn't own.
 *
 * Trusting `dominant.reservationId` presence as the authorization signal is
 * correct, not a client-side permission bypass: the server only populates
 * `CellEntry.reservationId` for an entry the caller owns OR one they hold a
 * quick-action permission for (`reservation.manage/force_release`) — see
 * `packages/contracts/src/schemas/availability.ts`'s `reservationId` field
 * comment and `apps/api/src/offers/availability.helpers.ts`'s
 * `resolveCanActOnOthersReservations`. The four quick-action buttons
 * themselves stay separately gated by their own `useHasPermission` checks
 * (`buildInspectGates`) — this only resolves WHICH reservation a click acts
 * on once a gate has already allowed the click.
 */
export function getOfferUnitActionableCell(
  units: UnitAvailabilityRow[],
  unitId: string,
  period: { start: string; end: string },
): AvailabilityCell | null {
  const entries = findOfferUnitEntries(units, unitId, period.start, period.end);
  const dominant = entries[0];
  if (!dominant || !dominant.reservationId) return null;
  return {
    start: period.start,
    end: period.end,
    status: dominant.ownership === 'mine' ? 'RESERVED-MINE' : 'RESERVED-OTHER',
    reservationId: dominant.reservationId,
    reservationStatus: toReservationStatus(dominant.status),
  };
}

/**
 * Ids (as strings, matching this module's own id-keying convention) of units
 * that carry an active reservation held by the current offer — scanned
 * across every period `units` covers, never restricted to a caller-supplied
 * rendered period subset. Used by the bulk-remove reservation-aware warning
 * (`useOfferAvailabilityGrids`'s dedicated, toggle/window-independent
 * reservation-check fetch), which must stay correct regardless of the
 * availability toggle or the pannable/collapsible rendered window
 * (bug-detection-sweep fix, offer-unified-table CU-86cbc3r8b) — the previous
 * implementation only checked the caller's currently RENDERED periods
 * against the fetch driving the visible columns, so it silently reported no
 * reservation whenever availability was toggled off (unitIds became empty)
 * or the reservation's dates fell outside the panned window.
 */
export function unitIdsWithOwnReservation(units: UnitAvailabilityRow[]): Set<string> {
  const ids = new Set<string>();
  for (const unit of units) {
    const hasOwn = unit.periods.some((period) => {
      const dominant = period.entries[0];
      return (
        dominant !== undefined &&
        dominant.ownership === 'mine' &&
        dominant.reservationId !== undefined
      );
    });
    if (hasOwn) ids.add(String(unit.unitId));
  }
  return ids;
}

/**
 * Builds an `EntriesGridData` grid keyed by MY rendered `[unitId][periodId]`
 * pairs (not the server's own period ids) — for `resolveAvailabilityMultiSelectCell`
 * and the multi-select popover, which look entries up by the caller's own
 * rendered period id. Each rendered period's entries come from whichever
 * server period overlaps it (see `findOfferUnitEntries`).
 */
export function buildOfferUnitEntriesGrid(
  units: UnitAvailabilityRow[],
  periods: { id: string; start: string; end: string }[],
): Record<string, Record<string, CellEntry[]>> {
  const grid: Record<string, Record<string, CellEntry[]>> = {};
  for (const unit of units) {
    const rowId = String(unit.unitId);
    grid[rowId] = Object.fromEntries(
      periods.map((period) => [
        period.id,
        findOfferUnitEntries(units, rowId, period.start, period.end),
      ]),
    );
  }
  return grid;
}

/**
 * Resolves a multi-select cell ref (keyed by OFFER-unit id, `ref.unitId`)
 * into a `UnifiedSelectCellResolution` for the shared `UnifiedSelectPopover`
 * — the offer-scoped analog of `resolveAvailabilityMultiSelectCell`, which
 * assumes `ref.unitId` already IS the media-unit id (true for the catalog,
 * not for this surface, where the row id is the offer_draft_units id).
 * `getMediaUnitId` resolves the offer-unit id to its media-unit id.
 */
export function resolveOfferMultiSelectCell(
  ref: MultiSelectCellRef,
  periods: { id: string; start: string; end: string }[],
  entriesGrid: Record<string, Record<string, CellEntry[]>>,
  getMediaUnitId: (unitId: string) => number | undefined,
): UnifiedSelectCellResolution | null {
  const period = periods.find((p) => p.id === ref.periodId);
  const mediaUnitId = getMediaUnitId(ref.unitId);
  if (!period || mediaUnitId === undefined) return null;
  const entries = entriesGrid[ref.unitId]?.[ref.periodId] ?? [];
  const ownEntry = entries.find(
    (e) => e.ownership === 'mine' && e.reservationId !== undefined && isHeldEntryStatus(e.status),
  );
  // Multi-select-eligible (task 4.1) but held by ANOTHER party — mirrors
  // `resolveAvailabilityMultiSelectCell`'s own `otherEntry` lookup so both resolvers agree
  // on which cells count as queueable-conflict for `resolveSelection`'s bucket split
  // (task 4.2, `reservation-mixed-range-and-target-expiry`).
  const otherEntry = entries.find((e) => e.ownership === 'other' && isHeldEntryStatus(e.status));
  return {
    mediaUnitId,
    dateFrom: new Date(`${period.start}T00:00:00.000Z`),
    dateTo: new Date(`${period.end}T00:00:00.000Z`),
    ownReservation:
      ownEntry?.reservationId !== undefined
        ? {
            id: ownEntry.reservationId,
            status: ownEntry.status as 'SOFT_RESERVATION' | 'HARD_RESERVATION' | 'OPTION',
            offerTitle: ownEntry.offerTitle,
            // Already the effective expiry for SOFT/HARD (see UnifiedSelectOwnReservation
            // docstring) — threaded so the SELECT popover can show the same deadline the
            // INSPECT popover would show for a non-own cell (availability-calendar spec).
            // Mirrors `resolveAvailabilityMultiSelectCell`
            // (inventory-availability-shared.tsx), the catalog's own analog of this function.
            ttlExpiresAt: ownEntry.ttlExpiresAt,
            // The hold's own full booking window — threaded so Zwolnij can detect
            // whether the selection under-covers it (CU-86cbh03pj).
            holdDateFrom: ownEntry.dateFrom,
            holdDateTo: ownEntry.dateTo,
          }
        : undefined,
    heldByOther: otherEntry !== undefined,
  };
}
